import { useEffect } from 'react';
import { useFormikContext } from 'formik';
import { AssigneePicker, CommonFormField } from '../../components';
import type { AssigneePickerProps } from '../../components/Dropdowns/AssigneePicker';
import { useRfiFormField } from '../RfiFormProvider';
import { Assignee } from '../../sdk';
import { RfiCreateFormValues } from '../types';
import { useProjectAssignees } from '../../hooks/useProjectAssignees';

type UserPickerFieldProps = {
  fieldName: keyof RfiCreateFormValues;
  label: string;
  maxAssignees?: AssigneePickerProps['maxAssignees'];
};

// Shared user-picker field used by Watchers and CoReviewers
export const UserPickerField = ({ fieldName, label, maxAssignees }: UserPickerFieldProps) => {
  const formik = useFormikContext<RfiCreateFormValues>();
  const { isFieldsMandatory, errorMessage, isSubmitting, fieldRef } = useRfiFormField(fieldName);
  const { allProjectAssignees } = useProjectAssignees();

  const handleSelectAssignee = (assignees: Assignee[]) => {
    formik.setFieldValue(fieldName, assignees);
  };

  const selectedAssignees = formik.values[fieldName] as Assignee[];

  // Remove pre-filled assignees that are no longer valid (e.g., removed from project after rfi-type prefill)
  useEffect(() => {
    const validAssignees = selectedAssignees.filter((assignee) =>
      allProjectAssignees.some((option) => option.id === assignee.id),
    );

    if (validAssignees.length !== selectedAssignees.length) {
      handleSelectAssignee(validAssignees);
    }
  }, [selectedAssignees, allProjectAssignees]);

  return (
    <CommonFormField
      label={label}
      required={isFieldsMandatory}
      error={isFieldsMandatory && errorMessage}
      ref={fieldRef}
    >
      <AssigneePicker
        onChange={handleSelectAssignee}
        selectedAssignees={selectedAssignees}
        assigneesOptions={allProjectAssignees}
        clearable
        disabled={isSubmitting}
        error={!!errorMessage}
        maxAssignees={maxAssignees}
      />
    </CommonFormField>
  );
};
